import { createContext, useContext } from "react";
import { SentientXLogo, Skeleton } from "@sx/ui";

/* What a workspace gate shows while its door answers: the same skeleton a `Region` draws
 * before its first read, so a gate and a region loading look like one thing. A navigation
 * never shows it — the router keeps the page being left until the next one can draw — so
 * it is drawn only where there is no page yet to keep. The status text is for screen
 * readers; sighted people read the skeleton.
 *
 * Before the shell itself has mounted — a reload waiting on the one identity read — it
 * draws the shell's own frame instead: the rail with the wordmark and the page's bar, at
 * their final sizes, so the shell paints once and the page fills in beneath it rather
 * than a page-wide skeleton giving way to a different layout. */
export function RouteFallback() {
  const content = (
    <div role="status" aria-live="polite" className="h-full p-6">
      <span className="sr-only">Loading</span>
      <Skeleton className="h-8 w-64" />
      <Skeleton className="mt-4 h-40 w-full" />
    </div>
  );
  if (useContext(ShellMounted)) return content;
  return (
    <div className="relative flex h-full min-w-0 flex-col overflow-hidden bg-background text-foreground">
      <div className="flex min-h-0 flex-1">
        <div
          aria-hidden="true"
          className="hidden w-52 shrink-0 flex-col border-r border-border bg-muted px-2 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:flex"
        >
          <span className="mb-3 self-start px-2">
            <SentientXLogo className="text-feature-title" />
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="h-11 shrink-0 border-b border-border pt-[env(safe-area-inset-top)]" />
          <div role="status" aria-live="polite" className="min-h-0 flex-1">
            <span className="sr-only">Loading</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Whether the console's shell is already on screen around a pending route. */
export const ShellMounted = createContext(false);

/** How the router draws a wait, named once so the router, the shell's route and the test
 *  that proves the behaviour use the same values. A navigation draws none — the page being
 *  left stays until the next can draw — so the router's default never presents one. */
export const NAVIGATION_WAIT = {
  defaultPendingMs: Number.POSITIVE_INFINITY,
  defaultPendingComponent: RouteFallback,
} as const;

/** The one wait that is drawn: the cold load, which has no old page to keep, paints the
 *  shell's frame at once. Set on the route above every page. */
export const COLD_LOAD_WAIT = {
  pendingMs: 0,
  pendingComponent: RouteFallback,
} as const;
